import {
  ACTION_NAVIGATE_SYNC,
  iAmReady,
  syncPathname,
  type Message,
  type NavigateSyncResponse,
} from '@tiendanube/nexo';
import { useEffect, useRef, type ReactElement } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { nexo } from './nexo';
import { DetalleCorrida } from './paginas/DetalleCorrida';
import { FormularioProgramacion } from './paginas/FormularioProgramacion';
import { Inicio } from './paginas/Inicio';
import { NuevaActualizacion } from './paginas/NuevaActualizacion';
import { Programaciones } from './paginas/Programaciones';

/** La app se sirve bajo /admin, así que el router trabaja con ese prefijo. */
const BASENAME = '/admin';

/**
 * Sincronización de rutas en los dos sentidos.
 *
 * - De la app al admin: `syncPathname` en cada cambio de ruta, para que la URL del
 *   navegador refleje dónde está parado el comerciante.
 * - Del admin a la app: la suscripción a `ACTION_NAVIGATE_SYNC` es la que hace andar los
 *   botones "atrás" y "adelante" del navegador.
 *
 * `iAmReady` se manda **después** de suscribirse. Al revés, un evento de navegación que
 * llegue en el medio se pierde.
 */
function PuenteDeNavegacion(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const anunciado = useRef(false);

  useEffect(() => {
    const desuscribir = nexo.suscribe(
      ACTION_NAVIGATE_SYNC,
      (mensaje: Message<NavigateSyncResponse>) => {
        const path = mensaje.payload?.path;
        if (typeof path !== 'string' || path === '') return;
        const interna = path.startsWith(BASENAME) ? path.slice(BASENAME.length) : path;
        navigate(interna === '' ? '/' : interna, { replace: mensaje.payload?.replace ?? false });
      },
    );

    if (!anunciado.current) {
      anunciado.current = true;
      iAmReady(nexo);
    }

    return desuscribir;
  }, [navigate]);

  useEffect(() => {
    syncPathname(nexo, `${BASENAME}${location.pathname}`);
  }, [location.pathname]);

  return null;
}

export function App(): ReactElement {
  return (
    <BrowserRouter basename={BASENAME}>
      <PuenteDeNavegacion />
      <Routes>
        <Route path="/" element={<Inicio />} />
        <Route path="/nueva" element={<NuevaActualizacion />} />
        <Route path="/corridas/:id" element={<DetalleCorrida />} />
        <Route path="/programaciones" element={<Programaciones />} />
        <Route path="/programaciones/nueva" element={<FormularioProgramacion />} />
        <Route path="/programaciones/:id/editar" element={<FormularioProgramacion />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
